import { VIDEO_URL } from 'Constants/env';

export const linkRegex =
  /(?:https?:\/\/)?(?:www\.)?(?:[\w-]+\.)+[a-z]{2,6}(?:\/[^\s]*)?/gi;

/**
 * Helper function to extract all links from message content
 * Matches URLs like: http://example.com, https://example.com, www.example.com, example.com
 * Excludes domains that are part of an email address (preceded by '@').
 */
const extractAllLinks = (text: string): string[] => {
  if (!text) {
    return [];
  }

  const matches = Array.from(text.matchAll(linkRegex));

  // Don't match if this looks like an email address (contains @ before the domain)
  const validLinks = matches.filter((match) => {
    const linkStartIndex = match.index;
    if (linkStartIndex === 0) {
      return true;
    }
    const previousChar = text[linkStartIndex - 1];
    return previousChar !== '@';
  });

  return validLinks.map((match) => match[0]);
};

/**
 * Extracts the last link from a text message
 */
export const extractLastLink = (text: string): string | null => {
  const links = extractAllLinks(text);
  return links.length ? links[links.length - 1] : null;
};

/**
 * Normalizes a URL by removing protocol, www, and trailing slash
 */
export const normalizeUrl = (url: string): string =>
  url.replace(/^https?:\/\//i, '').replace(/^www\./i, '');

/**
 * Prefixes a URL with `https://` if it doesn't already start with a protocol.
 *
 * Needed because bare domains (e.g. `google.com`) are otherwise treated by the
 * browser as relative paths instead of external links, breaking navigation.
 */
export const ensureUrlProtocol = (url: string): string =>
  /^https?:\/\//i.test(url) ? url : `https://${url}`;

/**
 * True for conference session URLs (e.g. https://host/video/{id}).
 * False for in-app video routes such as /video-app/history/{id}, which also
 * contain the "/video" substring and must not open via openVideoWindow.
 */
export const isVideoConferenceUrl = (
  href: string,
  videoUrl: string = VIDEO_URL
): boolean => {
  if (!href || !videoUrl) {
    return false;
  }

  return href.startsWith(videoUrl);
};

/**
 * Ensures conference URLs include auto_login so Electron can bootstrap
 * the video client session (same as navigateVideoConferenceToSession).
 */
export const ensureVideoAutoLogin = (href: string): string => {
  if (!href) {
    return href;
  }

  try {
    const url = new URL(href);
    if (!url.searchParams.has('auto_login')) {
      url.searchParams.set('auto_login', 'true');
    }
    return url.toString();
  } catch {
    return href;
  }
};

/**
 * Returns the in-app path for same-origin /video-app URLs (history, room, etc.),
 * or null when the href should not be handled as an internal video-app route.
 */
export const getVideoAppPath = (href: string): string | null => {
  if (!href || typeof window === 'undefined') {
    return null;
  }

  try {
    const url = new URL(href);
    if (url.origin !== window.location.origin) {
      return null;
    }
    if (!url.pathname.startsWith('/video-app')) {
      return null;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};
